/**
 * Local SQLite database wrapper using sql.js (WASM)
 *
 * Student names are stored LOCALLY only on the teacher's device.
 * This file handles database initialization, schema creation, and CRUD operations.
 */

import initSqlJs from "sql.js";
import type { Database, SqlJsStatic, SqlValue } from "sql.js";
import type {
  LocalClass,
  LocalStudent,
  CreateClassInput,
  CreateStudentInput,
  UpdateStudentInput,
} from "@/types/local-db.types";

// ============================================================================
// Constants
// ============================================================================

const SCHEMA_VERSION = "2";

const SCHEMA_SQL = `
-- Classes table
CREATE TABLE IF NOT EXISTS classes (
  id TEXT PRIMARY KEY,
  server_class_id TEXT,
  name TEXT NOT NULL,
  grade TEXT DEFAULT '',
  created_at TEXT DEFAULT (datetime('now')),
  updated_at TEXT DEFAULT (datetime('now'))
);

-- Students table with sheet number mapping
CREATE TABLE IF NOT EXISTS students (
  id TEXT PRIMARY KEY,
  class_id TEXT NOT NULL,
  sheet_number TEXT NOT NULL,
  name TEXT NOT NULL,
  created_at TEXT DEFAULT (datetime('now')),
  updated_at TEXT DEFAULT (datetime('now')),
  FOREIGN KEY (class_id) REFERENCES classes(id) ON DELETE CASCADE,
  UNIQUE(class_id, sheet_number)
);

-- Indexes for performance
CREATE INDEX IF NOT EXISTS idx_students_class ON students(class_id);
CREATE INDEX IF NOT EXISTS idx_students_sheet ON students(class_id, sheet_number);
CREATE INDEX IF NOT EXISTS idx_classes_server ON classes(server_class_id);

-- Metadata table for schema versioning
CREATE TABLE IF NOT EXISTS metadata (
  key TEXT PRIMARY KEY,
  value TEXT
);
`;

// ============================================================================
// SQL.js Initialization
// ============================================================================

let sqlPromise: Promise<SqlJsStatic> | null = null;

/**
 * Initialize sql.js with WASM. Cached after first call.
 */
export async function initializeSqlJs(): Promise<SqlJsStatic> {
  if (!sqlPromise) {
    // Use BASE_URL for deployment flexibility
    const baseUrl = import.meta.env.BASE_URL || "/";
    sqlPromise = initSqlJs({
      locateFile: (file: string) => `${baseUrl}sql-wasm/${file}`,
    });
  }
  return sqlPromise;
}

// ============================================================================
// Database Creation & Opening
// ============================================================================

/**
 * Create a new empty database with schema.
 */
export async function createNewDatabase(): Promise<Database> {
  const SQL = await initializeSqlJs();
  const db = new SQL.Database();

  // Initialize schema
  db.run(SCHEMA_SQL);

  // Set metadata
  db.run(
    "INSERT OR REPLACE INTO metadata (key, value) VALUES (?, ?)",
    ["schema_version", SCHEMA_VERSION]
  );
  db.run(
    "INSERT OR REPLACE INTO metadata (key, value) VALUES (?, ?)",
    ["app_name", "QuickGrade"]
  );
  db.run(
    "INSERT OR REPLACE INTO metadata (key, value) VALUES (?, ?)",
    ["created_at", new Date().toISOString()]
  );

  return db;
}

/**
 * Run database migrations if needed.
 */
function migrateDatabase(db: Database): void {
  // Get current schema version
  const versionResult = db.exec(
    "SELECT value FROM metadata WHERE key = 'schema_version'"
  );
  const currentVersion = versionResult.length > 0
    ? (versionResult[0].values[0][0] as string)
    : "1";

  // Migration from v1 to v2: Add server_class_id column
  if (currentVersion === "1") {
    console.log("Migrating database from v1 to v2...");

    // Add server_class_id column to classes table
    db.run("ALTER TABLE classes ADD COLUMN server_class_id TEXT");

    // Create index for server_class_id
    db.run("CREATE INDEX IF NOT EXISTS idx_classes_server ON classes(server_class_id)");

    // Update schema version
    db.run(
      "INSERT OR REPLACE INTO metadata (key, value) VALUES (?, ?)",
      ["schema_version", "2"]
    );

    console.log("Migration to v2 complete.");
  }
}

/**
 * Open a database from a File object (e.g., from file picker).
 */
export async function openDatabaseFromFile(file: File): Promise<Database> {
  const SQL = await initializeSqlJs();
  const buffer = await file.arrayBuffer();
  const db = new SQL.Database(new Uint8Array(buffer));

  // Verify it's a valid QuickGrade database
  const result = db.exec(
    "SELECT value FROM metadata WHERE key = 'app_name'"
  );
  if (result.length === 0 || result[0].values[0][0] !== "QuickGrade") {
    db.close();
    throw new Error("Invalid database file. This is not a QuickGrade database.");
  }

  // Run migrations if needed
  migrateDatabase(db);

  return db;
}

/**
 * Open a database from raw bytes.
 */
export async function openDatabaseFromBytes(
  bytes: Uint8Array
): Promise<Database> {
  const SQL = await initializeSqlJs();
  return new SQL.Database(bytes);
}

/**
 * Export database to a Blob for saving.
 */
export function exportDatabaseBlob(db: Database): Blob {
  const data = db.export();
  // Create a copy to ensure proper ArrayBuffer type
  const buffer = new Uint8Array(data).buffer;
  return new Blob([buffer], { type: "application/x-sqlite3" });
}

/**
 * Export database to Uint8Array.
 */
export function exportDatabaseBytes(db: Database): Uint8Array {
  return db.export();
}

// ============================================================================
// Class CRUD Operations
// ============================================================================

/**
 * Get all classes.
 */
export function getAllClasses(db: Database): LocalClass[] {
  const result = db.exec(`
    SELECT id, server_class_id, name, grade, created_at, updated_at
    FROM classes
    ORDER BY name ASC
  `);

  if (result.length === 0) return [];

  return result[0].values.map((row: SqlValue[]) => ({
    id: row[0] as string,
    serverClassId: row[1] as string | null,
    name: row[2] as string,
    grade: row[3] as string,
    createdAt: row[4] as string,
    updatedAt: row[5] as string,
  }));
}

/**
 * Get a single class by ID.
 */
export function getClassById(db: Database, id: string): LocalClass | null {
  const stmt = db.prepare(
    "SELECT id, server_class_id, name, grade, created_at, updated_at FROM classes WHERE id = ?"
  );
  stmt.bind([id]);

  if (stmt.step()) {
    const row = stmt.get();
    stmt.free();
    return {
      id: row[0] as string,
      serverClassId: row[1] as string | null,
      name: row[2] as string,
      grade: row[3] as string,
      createdAt: row[4] as string,
      updatedAt: row[5] as string,
    };
  }

  stmt.free();
  return null;
}

/**
 * Get a class by its server class ID.
 */
export function getClassByServerClassId(db: Database, serverClassId: string): LocalClass | null {
  const stmt = db.prepare(
    "SELECT id, server_class_id, name, grade, created_at, updated_at FROM classes WHERE server_class_id = ?"
  );
  stmt.bind([serverClassId]);

  if (stmt.step()) {
    const row = stmt.get();
    stmt.free();
    return {
      id: row[0] as string,
      serverClassId: row[1] as string | null,
      name: row[2] as string,
      grade: row[3] as string,
      createdAt: row[4] as string,
      updatedAt: row[5] as string,
    };
  }

  stmt.free();
  return null;
}

/**
 * Create a new class.
 */
export function createClass(db: Database, input: CreateClassInput): LocalClass {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();

  db.run(
    "INSERT INTO classes (id, server_class_id, name, grade, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)",
    [id, input.serverClassId || null, input.name, input.grade || "", now, now]
  );

  return {
    id,
    serverClassId: input.serverClassId || null,
    name: input.name,
    grade: input.grade || "",
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Update a class.
 */
export function updateClass(
  db: Database,
  id: string,
  input: Partial<CreateClassInput>
): void {
  const updates: string[] = [];
  const values: (string | null)[] = [];

  if (input.name !== undefined) {
    updates.push("name = ?");
    values.push(input.name);
  }
  if (input.grade !== undefined) {
    updates.push("grade = ?");
    values.push(input.grade);
  }
  if (input.serverClassId !== undefined) {
    updates.push("server_class_id = ?");
    values.push(input.serverClassId);
  }

  if (updates.length === 0) return;

  updates.push("updated_at = ?");
  values.push(new Date().toISOString());
  values.push(id);

  db.run(`UPDATE classes SET ${updates.join(", ")} WHERE id = ?`, values);
}

/**
 * Set the server class ID for a local class.
 * Used when syncing local classes to server.
 */
export function setServerClassId(
  db: Database,
  localClassId: string,
  serverClassId: string
): void {
  db.run(
    "UPDATE classes SET server_class_id = ?, updated_at = ? WHERE id = ?",
    [serverClassId, new Date().toISOString(), localClassId]
  );
}

/**
 * Get all classes that don't have a server class ID (need syncing).
 */
export function getUnsyncedClasses(db: Database): LocalClass[] {
  const result = db.exec(`
    SELECT id, server_class_id, name, grade, created_at, updated_at
    FROM classes
    WHERE server_class_id IS NULL
    ORDER BY name ASC
  `);

  if (result.length === 0) return [];

  return result[0].values.map((row: SqlValue[]) => ({
    id: row[0] as string,
    serverClassId: row[1] as string | null,
    name: row[2] as string,
    grade: row[3] as string,
    createdAt: row[4] as string,
    updatedAt: row[5] as string,
  }));
}

/**
 * Delete a class and all its students.
 */
export function deleteClass(db: Database, id: string): void {
  // Foreign key cascade should handle students, but let's be explicit
  db.run("DELETE FROM students WHERE class_id = ?", [id]);
  db.run("DELETE FROM classes WHERE id = ?", [id]);
}

// ============================================================================
// Student CRUD Operations
// ============================================================================

/**
 * Get all students for a class.
 */
export function getStudentsByClass(
  db: Database,
  classId: string
): LocalStudent[] {
  const stmt = db.prepare(`
    SELECT id, class_id, sheet_number, name, created_at, updated_at
    FROM students
    WHERE class_id = ?
    ORDER BY CAST(sheet_number AS INTEGER) ASC
  `);
  stmt.bind([classId]);

  const students: LocalStudent[] = [];
  while (stmt.step()) {
    const row = stmt.get();
    students.push({
      id: row[0] as string,
      classId: row[1] as string,
      sheetNumber: row[2] as string,
      name: row[3] as string,
      createdAt: row[4] as string,
      updatedAt: row[5] as string,
    });
  }
  stmt.free();

  return students;
}

/**
 * Get a student by sheet number within a class.
 */
export function getStudentBySheetNumber(
  db: Database,
  classId: string,
  sheetNumber: string
): LocalStudent | null {
  const stmt = db.prepare(`
    SELECT id, class_id, sheet_number, name, created_at, updated_at
    FROM students
    WHERE class_id = ? AND sheet_number = ?
  `);
  stmt.bind([classId, sheetNumber]);

  if (stmt.step()) {
    const row = stmt.get();
    stmt.free();
    return {
      id: row[0] as string,
      classId: row[1] as string,
      sheetNumber: row[2] as string,
      name: row[3] as string,
      createdAt: row[4] as string,
      updatedAt: row[5] as string,
    };
  }

  stmt.free();
  return null;
}

/**
 * Get the next available sheet number for a class.
 */
export function getNextSheetNumber(db: Database, classId: string): string {
  const stmt = db.prepare(`
    SELECT MAX(CAST(sheet_number AS INTEGER))
    FROM students
    WHERE class_id = ?
  `);
  stmt.bind([classId]);

  if (stmt.step()) {
    const maxNum = stmt.get()[0] as number | null;
    stmt.free();
    return maxNum ? String(maxNum + 1) : "1";
  }

  stmt.free();
  return "1";
}

/**
 * Create a new student.
 */
export function createStudent(
  db: Database,
  input: CreateStudentInput
): LocalStudent {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const sheetNumber = input.sheetNumber || getNextSheetNumber(db, input.classId);

  db.run(
    "INSERT INTO students (id, class_id, sheet_number, name, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)",
    [id, input.classId, sheetNumber, input.name, now, now]
  );

  return {
    id,
    classId: input.classId,
    sheetNumber,
    name: input.name,
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Update a student.
 */
export function updateStudent(
  db: Database,
  id: string,
  input: UpdateStudentInput
): void {
  const updates: string[] = [];
  const values: (string | null)[] = [];

  if (input.name !== undefined) {
    updates.push("name = ?");
    values.push(input.name);
  }
  if (input.sheetNumber !== undefined) {
    updates.push("sheet_number = ?");
    values.push(input.sheetNumber);
  }

  if (updates.length === 0) return;

  updates.push("updated_at = ?");
  values.push(new Date().toISOString());
  values.push(id);

  db.run(`UPDATE students SET ${updates.join(", ")} WHERE id = ?`, values);
}

/**
 * Delete a student.
 */
export function deleteStudent(db: Database, id: string): void {
  db.run("DELETE FROM students WHERE id = ?", [id]);
}

/**
 * Bulk create students (e.g., from CSV import).
 */
export function bulkCreateStudents(
  db: Database,
  classId: string,
  students: Array<{ name: string; sheetNumber?: string }>
): LocalStudent[] {
  const created: LocalStudent[] = [];

  for (const student of students) {
    const newStudent = createStudent(db, {
      classId,
      name: student.name,
      sheetNumber: student.sheetNumber,
    });
    created.push(newStudent);
  }

  return created;
}

// ============================================================================
// Sheet Number Mapping
// ============================================================================

/**
 * Build a map of sheet number -> student name for quick lookups.
 */
export function buildSheetNumberMap(
  db: Database,
  classId: string
): Map<string, { name: string; id: string }> {
  const students = getStudentsByClass(db, classId);
  const map = new Map<string, { name: string; id: string }>();

  for (const student of students) {
    map.set(student.sheetNumber, {
      name: student.name,
      id: student.id,
    });
  }

  return map;
}

/**
 * Get student name by sheet number.
 */
export function getStudentNameBySheetNumber(
  db: Database,
  classId: string,
  sheetNumber: string
): string | null {
  const student = getStudentBySheetNumber(db, classId, sheetNumber);
  return student?.name ?? null;
}

// ============================================================================
// Statistics
// ============================================================================

/**
 * Get database statistics.
 */
export function getDatabaseStats(
  db: Database
): { classCount: number; studentCount: number } {
  const classResult = db.exec("SELECT COUNT(*) FROM classes");
  const studentResult = db.exec("SELECT COUNT(*) FROM students");

  return {
    classCount: classResult.length > 0 ? (classResult[0].values[0][0] as number) : 0,
    studentCount: studentResult.length > 0 ? (studentResult[0].values[0][0] as number) : 0,
  };
}
